import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DialogDeleteCodeConfirmationComponent } from '@app/modules/smart-devices/dialogs/dialog-delete-code-confirmation/dialog-delete-code-confirmation.component';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Code } from '../../data/smart-devices.interface';
import { SmartDevicesService } from '../../service/smart-devices.service';

@Component({
  standalone: false,
  selector: 'sbnb-dialog-code-reservation-details',
  templateUrl: './dialog-code-reservation-details.component.html',
  styleUrls: ['./dialog-code-reservation-details.component.scss'],
})
export class DialogCodeReservationDetailsComponent implements OnInit {
  DATE_FORMAT = "MMM d - h:mm aaaaa'm'";

  deleting = false;

  hasWritePermission$ = this.permsService.hasAccessTo(Features.PERM_SMARTLOCKS_DASHBOARD_WRITE);

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: { reservation: Code[] },
    public dialogRef: MatDialogRef<DialogCodeReservationDetailsComponent>,
    private datePipe: DatePipe,
    private toast: ToastNotificationsService,
    private smartDevicesService: SmartDevicesService,
    private dialog: MatDialog,
    private permsService: UserPermissionsService
  ) { }

  ngOnInit(): void {
    // We'll do this in here, as we're transforming purely for the timeline component in the template and it uses a slightly specialised data format
    this.dialogData.reservation.forEach((code) => {
      // Sort status by date, most recent at the top
      code.statuses.sort((a, b) => (b.estimated_completion_at > a.estimated_completion_at ? 1 : -1));

      if (!code.computed) {
        code.computed = {};
      }

      code.computed.timeline = code.statuses.map((status, index) => {
        return {
          title: status.name,
          subtitle: this.datePipe.transform(status.estimated_completion_at, "MMM d - h:mm aaaaa'm'"),
          cssClass: status.is_latest ? 'is-active-timeline' : '',
          isFilled: status.is_latest || index > code.statuses.findIndex((s) => s.is_latest),
        };
      });
    });
  }

  codeCopied() {
    this.toast.open('Copied to clipboard', 'Dismiss', NotificationType.Success, 2500);
  }

  onDeviceLockToggled(event: MatSlideToggleChange, device: Code['device']) {
    if (!device.computed) {
      device.computed = {};
    }

    device.computed.changingLockStatus = true;

    if (event.checked) {
      this.smartDevicesService.lockDevice(device.id).subscribe(
        () => {
          device.computed.changingLockStatus = false;
          device.device_properties.locked = true;
        },
        () => {
          device.computed.changingLockStatus = false;
        }
      );
    } else {
      this.smartDevicesService.unlockDevice(device.id).subscribe(
        () => {
          device.computed.changingLockStatus = false;
          device.device_properties.locked = false;
        },
        () => {
          device.computed.changingLockStatus = false;
        }
      );
    }
  }

  onLockClicked(device: Code['device']) {
    this.dialogRef.close({ openDeviceModal: true, deviceId: device.id });
  }

  onDeleteClicked() {
    const dialogRef = this.dialog.open(DialogDeleteCodeConfirmationComponent, {
      maxWidth: '320px',
      panelClass: 'dialog__no-padding',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteCode();
      }
    });
  }

  deleteCode() {
    const propertyId = this.dialogData.reservation[0].properties[0].id;
    const reservationId = this.dialogData.reservation[0].reservation.reservation_id;

    this.deleting = true;

    this.smartDevicesService.deleteCode(propertyId, reservationId).subscribe(
      () => {
        this.deleting = false;
        this.toast.open(`Code deleted`, 'Dismiss', NotificationType.Success, 2500);
        this.smartDevicesService.refreshReservationsAndDevicesFromApi$.next(null);
        this.dialogRef.close();
      },
      () => {
        this.deleting = false;
      }
    );
  }

  onEditCodeClicked() {
    this.dialogRef.close({
      openEditModal: true,
    });
  }
}
